// Browser/device notification utilities for new messages

export class NotificationService {
  private static permission: NotificationPermission = 'default';

  /**
   * Request notification permission from the user
   */
  static async requestPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      console.log('This browser does not support notifications');
      return false;
    }

    if (Notification.permission === 'granted') {
      this.permission = 'granted';
      return true;
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      this.permission = permission;
      return permission === 'granted';
    }

    return false;
  }

  /**
   * Show a notification for a new message
   */
  static async showMessageNotification(
    senderName: string,
    messagePreview: string,
    senderId: string
  ): Promise<void> {
    // Don't show if permission not granted
    if (Notification.permission !== 'granted') {
      return;
    }

    // Don't show if page is focused (user is already looking at it)
    if (document.hasFocus()) {
      return;
    }

    const title = `New message from ${senderName}`;
    const options: NotificationOptions = {
      body: messagePreview.substring(0, 100), // Truncate long messages
      icon: '/logo192.png',
      badge: '/logo192.png',
      tag: `message-${senderId}`, // Replace previous notification from same sender
      requireInteraction: false, // Auto-dismiss after a few seconds
      data: {
        userId: senderId,
        url: `/dashboard/messages?user=${senderId}`
      }
    };

    // If service worker is available, use it for better background support
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.ready.then((registration) => {
        registration.showNotification(title, options);
      });
    } else {
      // Fallback to regular notification
      const notification = new Notification(title, options);

      // Navigate to messages when clicked
      notification.onclick = (event) => {
        event.preventDefault();
        window.focus();
        window.location.href = `/dashboard/messages?user=${senderId}`;
        notification.close();
      };
    }
  }

  /**
   * Check if notifications are supported and enabled
   */
  static isSupported(): boolean {
    return 'Notification' in window;
  }

  /**
   * Get current permission status
   */
  static getPermission(): NotificationPermission {
    return Notification.permission;
  }
}
